import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { tagsApi } from '../../lib/api';
import type { Tag } from '../../types/tag';
import { useAuthStore } from '../../store/authStore';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { TagChips } from './TagChips';

/**
 * Minimal tag management view (v1): lists the tags visible to the current user,
 * lets anyone retire a tag (soft delete, behind a confirm), and lets managers
 * promote a private tag to shared. Inline rename/recolor is a deliberate v1.1
 * follow-up and is intentionally not built here.
 *
 * Rendered inside the "Manage tags" SlideOver on the Lists page.
 */
export const TagManager = () => {
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(true);
  const [pendingRetire, setPendingRetire] = useState<Tag | null>(null);
  const [promotingId, setPromotingId] = useState<string | null>(null);

  const hasAnyRole = useAuthStore((s) => s.hasAnyRole);
  const isManager = hasAnyRole('MANAGER', 'ADMIN');

  const reload = () => {
    setLoading(true);
    return tagsApi
      .list()
      .then((next: Tag[]) => setTags(next))
      .catch(() => setTags([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    reload();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-lime-600 dark:text-lime-400" />
      </div>
    );
  }

  if (tags.length === 0) {
    return (
      <div className="py-12 text-center text-sm text-gray-500 dark:text-gray-400">
        No tags yet.
      </div>
    );
  }

  return (
    <>
      <div className="space-y-2">
        {tags.map((t) => (
          <div
            key={t.id}
            className="flex items-center gap-2 rounded-md border border-gray-200 dark:border-gray-700 px-3 py-2"
          >
            <div className="flex min-w-0 items-center gap-2">
              <span className="shrink-0">
                <TagChips tags={[t]} />
              </span>
              <span className="min-w-0 truncate text-sm text-gray-700 dark:text-gray-300">
                {t.name}
              </span>
            </div>
            <span
              className={`shrink-0 text-xs ${
                t.isShared
                  ? 'text-green-600 dark:text-green-400'
                  : 'text-gray-400 dark:text-gray-500'
              }`}
            >
              {t.isShared ? 'shared' : 'private'}
            </span>
            <div className="ml-auto flex shrink-0 items-center gap-2">
              {isManager && !t.isShared && (
                <button
                  type="button"
                  className="btn-secondary text-xs"
                  disabled={promotingId === t.id}
                  onClick={async () => {
                    setPromotingId(t.id);
                    try {
                      await tagsApi.promote(t.id);
                      await reload();
                    } catch (e: any) {
                      toast.error(
                        e?.response?.data?.message || 'Could not promote tag'
                      );
                    } finally {
                      setPromotingId(null);
                    }
                  }}
                >
                  {promotingId === t.id ? (
                    <span className="flex items-center gap-1">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Promoting…
                    </span>
                  ) : (
                    'Promote'
                  )}
                </button>
              )}
              <button
                type="button"
                className="btn-danger text-xs"
                onClick={() => setPendingRetire(t)}
              >
                Retire
              </button>
            </div>
          </div>
        ))}
      </div>

      <ConfirmDialog
        open={pendingRetire !== null}
        title="Retire tag"
        message={
          pendingRetire
            ? `Retire the tag "${pendingRetire.name}"? It will be removed from the vocabulary and no longer available to apply.`
            : ''
        }
        confirmLabel="Retire"
        danger
        onConfirm={async () => {
          if (pendingRetire) {
            await tagsApi.retire(pendingRetire.id);
            await reload();
          }
          setPendingRetire(null);
        }}
        onCancel={() => setPendingRetire(null)}
      />
    </>
  );
};
